import { useMutation, useQuery } from "@tanstack/react-query";
import { AuthContextValue, useAuth } from "../../providers/AuthProvider";
import axiosBaseURL from "..";
import { PaginationModel } from "../../models/paginationModel";

export const useGetAllPrePlanting = (options?: {
  search?: string;
  page?: number;
  size?: number;
}) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: [
      "pre-planting",
      options?.search,
      options?.page,
      options?.size,
    ].filter(Boolean),
    staleTime: 120_000,
    queryFn: async () => {
      const queryParams = new URLSearchParams();

      if (options?.page !== undefined)
        queryParams.append("page", options.page.toString());
      if (options?.size !== undefined)
        queryParams.append("size", options.size.toString());
      if (options?.search) queryParams.append("search", options.search);

      const url = `/v1/scouting/pre-planting?${queryParams.toString()}`;

      const { data } = await axiosBaseURL.get(url, {
        headers: { header: `${user?.userId}/${user?.token}` },
      });

      return data;
    },
  });
};

export const useGetAllPlanting = (options?: {
  search?: string;
  page?: number;
  size?: number;
}) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: [
      "planting",
      options?.search,
      options?.page,
      options?.size,
    ].filter(Boolean),
    staleTime: 120_000,
    queryFn: async () => {
      const queryParams = new URLSearchParams();

      if (options?.page !== undefined)
        queryParams.append("page", options.page.toString());
      if (options?.size !== undefined)
        queryParams.append("size", options.size.toString());
      if (options?.search) queryParams.append("search", options.search);

      const url = `/v1/scouting/planting?${queryParams.toString()}`;

      const { data } = await axiosBaseURL.get(url, {
        headers: { header: `${user?.userId}/${user?.token}` },
      });

      return data;
    },
  });
};

export const useGetAllPreHarvesting = (options?: {
  search?: string;
  page?: number;
  size?: number;
}) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: [
      "get-all-pre-harvesting",
      options?.search,
      options?.page,
      options?.size,
    ].filter(Boolean),
    staleTime: 120_000,
    queryFn: async () => {
      const queryParams = new URLSearchParams();

      if (options?.page !== undefined)
        queryParams.append("page", options.page.toString());
      if (options?.size !== undefined)
        queryParams.append("size", options.size.toString());
      if (options?.search) queryParams.append("search", options.search);

      const url = `/v1/scouting/preharvest?${queryParams.toString()}`;

      const { data } = await axiosBaseURL.get(url, {
        headers: { header: `${user?.userId}/${user?.token}` },
      });

      return data;
    },
  });
};

export const useGetAllHarvestingList = () => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["get harvesting"],
    staleTime: 1200,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`v1/scouting/harvesting`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
  });
};

export const useGetAllGrowth = (options?: {
  search?: string;
  page?: number;
  size?: number;
}) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: [
      "get-all-plant-growth",
      options?.search,
      options?.page,
      options?.size,
    ].filter(Boolean),
    staleTime: 120_000,
    queryFn: async () => {
      const queryParams = new URLSearchParams();

      if (options?.page !== undefined)
        queryParams.append("page", options.page.toString());
      if (options?.size !== undefined)
        queryParams.append("size", options.size.toString());
      if (options?.search) queryParams.append("search", options.search);

      const url = `/v1/scouting/growth?${queryParams.toString()}`;

      const { data } = await axiosBaseURL.get(url, {
        headers: { header: `${user?.userId}/${user?.token}` },
      });

      return data;
    },
  });
};

export const useGetPlantGrowthById = (id: number) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["useGetPlantGrowthById", id],
    staleTime: 120,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`v1/scouting/growth/${id}`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
  });
};

export const useGetByPrePlanting = (id: number) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["useGetByPrePlanting", id],
    staleTime: 120,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`v1/scouting/pre-planting/${id}`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
  });
};

export const useGetByPlanting = (id: number) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["useGetByPlanting", id],
    staleTime: 120,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`v1/scouting/planting/${id}`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
  });
};

export const useGetByPreHarvesting = (id: number) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["useGetByPreHarvesting", id],
    staleTime: 120,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`v1/scouting/preharvest/${id}`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
  });
};

export const useUpdatePrePlanting = () => {
  const { user } = useAuth() as AuthContextValue;
  return useMutation({
    mutationFn: async (data: { status: number; id: number }) => {
      const result = await axiosBaseURL.put(
        `v1/scouting/pre-planting/${data?.id}`,
        data,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        }
      );

      return result;
    },
  });
};

export const useUpdatePlanting = () => {
  const { user } = useAuth() as AuthContextValue;
  return useMutation({
    mutationFn: async (data: { id: number }) => {
      const result = await axiosBaseURL.put(
        `v1/scouting/planting/${data?.id}`,
        data,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        }
      );

      return result;
    },
  });
};

export const useUpdatePlantGrowth = () => {
  const { user } = useAuth() as AuthContextValue;
  return useMutation({
    mutationFn: async (data: { id: number }) => {
      const result = await axiosBaseURL.put(
        `v1/scouting/growth/${data?.id}`,
        data,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        }
      );

      return result;
    },
  });
};

export const useUpdatePreHarvesting = () => {
  const { user } = useAuth() as AuthContextValue;
  return useMutation({
    mutationFn: async (data: { id: number }) => {
      const result = await axiosBaseURL.put(
        `v1/scouting/preharvest/${data?.id}`,
        data,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        }
      );

      return result;
    },
  });
};

export const useUpdateHarvesting = () => {
  const { user } = useAuth() as AuthContextValue;
  return useMutation({
    mutationFn: async (data: { status: number; id: number }) => {
      const result = await axiosBaseURL.put(
        `v1/scouting/harvesting/${data?.id}`,
        data,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        }
      );

      return result;
    },
  });
};
